import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Flex, Text } from "@chakra-ui/react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { AppBar } from "../../../components/mobileUi";
import {
  DesktopBack,
  DesktopPage,
  DesktopPanel,
  useIsDesktop,
} from "../../../components/desktopUi";
import {
  CenterSpinner,
  EmptyText,
  ExpandableCard,
  M,
  RowKV,
  SummaryCard,
} from "../../../components/managementUi";
import { formatMoneyUS } from "../../../utils/financeFormat";
import {
  getIncomeSummaryAreaLockingDetailApi,
  getIncomeSummaryBlockLockingDetailApi,
} from "../../../api/incomeManager.api";

// Khớp app: management/income/{area|block}/locking-detail.tsx
// Danh sách nhân viên hưởng thu nhập của 1 căn trong vùng/khối.
const KINDS = {
  vung: { api: getIncomeSummaryAreaLockingDetailApi, idParam: "saleAreaId", groupLabel: "vùng" },
  khoi: { api: getIncomeSummaryBlockLockingDetailApi, idParam: "saleBlockId", groupLabel: "khối" },
};

const IncomeLockingEmployeeDetail = () => {
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();
  const { kind, id } = useParams();
  const [search] = useSearchParams();
  const lockingId = search.get("lockingId") || "";
  const apartmentCode = search.get("apartmentCode") || "";
  const groupName = search.get("name") || "";
  const month = search.get("month") || "";
  const year = search.get("year") || "";
  const cfg = KINDS[kind];

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const reqId = useRef(0);

  const load = useCallback(async () => {
    if (!cfg) return;
    reqId.current += 1;
    const rid = reqId.current;
    setLoading(true);
    setExpanded({});
    try {
      const params = {};
      if (id) params[cfg.idParam] = id;
      if (lockingId) params.lockingId = lockingId;
      if (month) params.month = Number(month);
      if (year) params.year = Number(year);
      const data = await cfg.api(params);
      if (rid !== reqId.current) return;
      setItems(data?.items || []);
    } catch {
      if (rid === reqId.current) setItems([]);
    } finally {
      if (rid === reqId.current) setLoading(false);
    }
  }, [cfg, id, lockingId, month, year]);

  useEffect(() => {
    load();
  }, [load]);

  if (!cfg) {
    return (
      <Box maxW="480px" mx="auto">
        <AppBar title="Chi tiết thu nhập nhân viên" />
        <EmptyText text="Không tìm thấy dữ liệu" />
      </Box>
    );
  }

  const pageBody = (
    <>
      <Box px="16px" pt="12px">
        <SummaryCard text="TỔNG SỐ NHÂN VIÊN" total={items.length} />

        <Text fontSize="14px" fontWeight={600} color={M.baseContent} mt="16px" mb="14px">
          {`Danh sách thu nhập nhân viên căn ${apartmentCode || "-"} ${cfg.groupLabel} ${
            groupName || "-"
          } tháng ${month || "-"} năm ${year || "-"}`}
        </Text>

        {loading ? (
          <CenterSpinner />
        ) : items.length === 0 ? (
          <EmptyText />
        ) : (
          <Flex direction="column" gap="12px">
            {items.map((it, i) => {
              const key = it?.employeeId || i;
              return (
                <ExpandableCard
                  key={key}
                  open={!!expanded[key]}
                  onToggle={() => setExpanded((p) => ({ ...p, [key]: !p[key] }))}
                  header={
                    <Text fontSize="14px" fontWeight={600} color={M.baseContent} noOfLines={2}>
                      {it?.employee?.fullName || "-"} - {it?.employee?.code || "-"}
                    </Text>
                  }
                >
                  <RowKV label="Chức vụ" value={it?.jobPosition?.name || "-"} />
                  <RowKV label="Khối" value={it?.saleArea?.name || "-"} />
                  <RowKV label="Vùng" value={it?.saleArea?.name || "-"} />
                  <RowKV label="Chi nhánh" value={it?.branch?.name || "-"} />
                  <Box h="1px" bg={M.primary} opacity={0.4} />
                  <RowKV
                    label="Thu nhập đối chiếu"
                    value={`${formatMoneyUS(it?.totalIncome || 0)} VNĐ`}
                    valueColor={M.primary}
                    labelColor={M.primary}
                  />
                </ExpandableCard>
              );
            })}
          </Flex>
        )}
      </Box>
    </>
  );

  // ===================== Bản WEB (desktop) =====================
  if (isDesktop) {
    return (
      <DesktopPage title="Chi tiết thu nhập nhân viên" right={<DesktopBack onClick={() => navigate(-1)} />}>
        <DesktopPanel p="0" pb="24px">
          {pageBody}
        </DesktopPanel>
      </DesktopPage>
    );
  }

  return (
    <Box maxW="480px" mx="auto" pb="40px">
      <AppBar title="Chi tiết thu nhập nhân viên" />
      {pageBody}
    </Box>
  );
};

export default IncomeLockingEmployeeDetail;
